import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { Input } from '@app/components/common/inputs/Input/Input';
import { notificationController } from '@app/controllers/notificationController';
import { useResetFormOnCloseModal } from '@app/components/forms/ControlForm/useResetFormOnCloseModal';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { Upload } from '@app/components/common/Upload/Upload';
import { Button } from '@app/components/common/buttons/Button/Button';
import { UploadOutlined } from '@ant-design/icons';
import { resizeThumbnail } from '@app/utils/utils';
import { InputNumber } from '@app/components/common/inputs/InputNumber/InputNumber';
import { normFile } from '@app/utils/file';
import { Hint } from '../ui/Hint';
import {
  InspirationModel,
  updateInspiration,
  UpdateInspirationData,
  updateInspirationImage,
} from '@app/api/inspiration.api';

interface CreateCouponModalProps {
  data?: InspirationModel;
  open: boolean;
  onCancel: () => void;
  onFinish: () => void;
}

interface UpdateFormData {
  link: string;
  order: number;
  image: File[];
}

export const UpdateInspirationModal: React.FC<CreateCouponModalProps> = ({ data, open, onCancel, onFinish }) => {
  const [form] = BaseForm.useForm();
  const { t } = useTranslation();

  const { isModelsLoading, isActionLoading } = useAppSelector((state) => state.model);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        link: data.link,
        order: data.order,
      });
    }
  }, [data, open]);

  useResetFormOnCloseModal({
    form,
    open,
  });

  const onOk = async () => {
    form.submit();
  };

  const onFormCancel = async () => {
    onCancel && onCancel();
  };

  const handleSubmit = (values: UpdateFormData) => {
    if (!data?.id) return;

    const _imageFile: File | any = values.image[0];
    const _data: UpdateInspirationData = {
      link: values.link,
      order: values.order,
    };

    updateInspiration(data.id, _data).then(async () => {
      try {
        if (_imageFile) {
          await updateInspirationImage(data.id, _imageFile);
        }
        notificationController.success({
          message: 'Update the inspiration',
          description: 'Successfully',
        });
        onFinish();
      } catch (e) {
        notificationController.success({
          message: 'Update the inspiration',
          description: 'Successfully',
        });
      }
    });
  };

  const imageDummyRequest = async (options: { file: any; onSuccess?: any; onError?: any; onProgress?: any }) => {
    const { onSuccess, onError, file } = options;
    form.setFieldsValue({ image: [file] });

    try {
      onSuccess('Ok');
    } catch (err) {
      onError({ err });
    }
  };

  return (
    <Modal title="Update Inspiration" open={open} onOk={onOk} onCancel={onFormCancel} confirmLoading={isActionLoading}>
      <BaseForm form={form} layout="vertical" name="update-inspiration" onFinish={handleSubmit}>
        <BaseForm.Item
          name="image"
          label="Image"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          initialValue={[]}
          rules={[{ required: false }]}
        >
          {data && data.imageUrl && (
            <img src={data.imageUrl} alt="thumbnail" style={{ width: '150px', marginBottom: '10px' }} />
          )}
          <Upload accept="image/*" maxCount={1} customRequest={imageDummyRequest} listType="picture">
            <Button type="default" icon={<UploadOutlined />}>
              {t('forms.validationFormLabels.clickToUpload')}
            </Button>
          </Upload>
          <Hint>{t('thumbnailUpload.uploadHint')}</Hint>
        </BaseForm.Item>
        <BaseForm.Item name="link" label="Link" rules={[{ required: true, message: t('common.requiredField') }]}>
          <Input />
        </BaseForm.Item>
        <BaseForm.Item name="order" label="Order" rules={[{ required: true, message: t('common.requiredField') }]}>
          <InputNumber />
        </BaseForm.Item>
      </BaseForm>
    </Modal>
  );
};
